import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
    Plus, Package, Edit, Trash2, Minus, AlertCircle, Building2,
    Users, Clock, Shield, Phone, FileText, Search, History,
    Zap, ChevronDown, ChevronUp, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resourceTypes = [
    { value: 'couverture', label: 'Couvertures' },
    { value: 'nourriture', label: 'Nourriture' },
    { value: 'eclairage', label: 'Eclairage' },
    { value: 'generateur', label: 'Generateurs' },
    { value: 'lit', label: 'Lits de camp' },
    { value: 'vehicule', label: 'Vehicules' },
    { value: 'grue', label: 'Grue' },
    { value: 'tracteur', label: 'Tracteur' },
    { value: 'pompe', label: 'Pompe' },
    { value: 'materiel_lourd', label: 'Materiel lourd' },
    { value: 'materiel_medical', label: 'Materiel medical' },
    { value: 'communication', label: 'Communication' },
    { value: 'autre', label: 'Autre' }
];

const statusConfig = {
    disponible: { label: 'Disponible', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    mobilise: { label: 'Mobilise', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    indisponible: { label: 'Indisponible', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    maintenance: { label: 'Maintenance', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' }
};

const emptyForm = {
    name: '', type: 'autre', category: 'interne', quantity: 1, quantity_available: 1,
    center_id: '', location_name: '', notes: '', status: 'disponible',
    organization_name: '', contact_name: '', contact_phone: '',
    availability_delay: '', prerequisites: '', contract_conditions: ''
};

const ResourcesPage = () => {
    const { token, isChefDispositif, canManageResources, canConsumeResources } = useAuth();
    const [resources, setResources] = useState([]);
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('interne');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [detailResource, setDetailResource] = useState(null);
    const [consumeDialogOpen, setConsumeDialogOpen] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [consumeQty, setConsumeQty] = useState(1);
    const [formData, setFormData] = useState({ ...emptyForm });
    const [searchTerm, setSearchTerm] = useState('');
    const [historyDialog, setHistoryDialog] = useState(false);
    const [mobilizationHistory, setMobilizationHistory] = useState([]);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [resRes, centersRes] = await Promise.all([
                axios.get(`${API}/resources`, { headers }),
                axios.get(`${API}/care-centers`, { headers }).catch(() => ({ data: [] }))
            ]);
            setResources(resRes.data);
            setCenters(centersRes.data);
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await axios.get(`${API}/resources/mobilization-history`, { headers });
            setMobilizationHistory(res.data);
        } catch (e) { toast.error('Erreur'); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (!payload.center_id) delete payload.center_id;
            if (editingResource) {
                await axios.put(`${API}/resources/${editingResource.id}`, payload, { headers });
                toast.success('Ressource mise a jour');
            } else {
                await axios.post(`${API}/resources`, payload, { headers });
                toast.success('Ressource ajoutee');
            }
            setDialogOpen(false);
            setEditingResource(null);
            setFormData({ ...emptyForm });
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette ressource ?')) return;
        try {
            await axios.delete(`${API}/resources/${id}`, { headers });
            toast.success('Supprimee');
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const handleConsume = async () => {
        if (!selectedResource) return;
        try {
            await axios.put(`${API}/resources/${selectedResource.id}/consume?quantity=${consumeQty}`, {}, { headers });
            toast.success('Ressource consommee');
            setConsumeDialogOpen(false);
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const handleMobilize = async (id) => {
        try {
            const res = await axios.put(`${API}/resources/${id}/mobilize`, {}, { headers });
            toast.success(res.data.message);
            fetchData();
        } catch (e) { toast.error('Erreur'); }
    };

    const openEdit = (r) => {
        setEditingResource(r);
        setFormData({
            name: r.name, type: r.type, category: r.category || 'interne',
            quantity: r.quantity, quantity_available: r.quantity_available,
            center_id: r.center_id || '', location_name: r.location_name || '',
            notes: r.notes || '', status: r.status || 'disponible',
            organization_name: r.organization_name || '',
            contact_name: r.contact_name || '', contact_phone: r.contact_phone || '',
            availability_delay: r.availability_delay || '',
            prerequisites: r.prerequisites || '',
            contract_conditions: r.contract_conditions || ''
        });
        setDialogOpen(true);
    };

    const openCreate = (cat) => {
        setEditingResource(null);
        setFormData({ ...emptyForm, category: cat });
        setDialogOpen(true);
    };

    const filtered = useMemo(() => {
        let list = resources.filter(r => (r.category || 'interne') === tab);
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            list = list.filter(r =>
                r.name.toLowerCase().includes(lower) ||
                r.type.toLowerCase().includes(lower) ||
                (r.organization_name || '').toLowerCase().includes(lower)
            );
        }
        return list;
    }, [resources, tab, searchTerm]);

    const tabCounts = useMemo(() => ({
        interne: resources.filter(r => (r.category || 'interne') === 'interne').length,
        contrat_externe: resources.filter(r => r.category === 'contrat_externe').length,
        association: resources.filter(r => r.category === 'association').length
    }), [resources]);

    const typeLabel = (t) => resourceTypes.find(rt => rt.value === t)?.label || t;
    const centerName = (id) => centers.find(c => c.id === id)?.name || '';

    if (loading) return <div className="p-6 text-center text-zinc-500">Chargement...</div>;

    return (
        <div className="page-container" data-testid="resources-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="page-title">Gestion des Moyens</h1>
                    <p className="page-subtitle">Internes, contrats externes et associations</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setHistoryDialog(true); fetchHistory(); }}
                        className="text-xs text-zinc-400" data-testid="history-btn">
                        <History className="w-3.5 h-3.5 mr-1" /> Historique mobilisations
                    </Button>
                    {canManageResources && (
                        <Button onClick={() => openCreate(tab)} className="tactical-btn" data-testid="add-resource-btn">
                            <Plus className="w-4 h-4 mr-2" /> Ajouter
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 p-1 bg-zinc-800/50 rounded-lg w-fit" data-testid="resource-tabs">
                {[
                    { key: 'interne', label: 'Moyens Internes', icon: Package },
                    { key: 'contrat_externe', label: 'Contrats Externes', icon: Building2 },
                    { key: 'association', label: 'Associations', icon: Users }
                ].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-all ${
                            tab === t.key ? 'bg-cyan-600 text-white shadow' : 'text-zinc-400 hover:text-white'
                        }`}
                        data-testid={`tab-${t.key}`}>
                        <t.icon className="w-4 h-4" />
                        {t.label} ({tabCounts[t.key]})
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="relative max-w-xs mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
                    data-testid="resource-search" />
            </div>

            {/* Resource Table */}
            <Card className="content-card" data-testid="resources-table">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">Nom</TableHead>
                                <TableHead className="text-zinc-400">Type</TableHead>
                                {tab !== 'interne' && <TableHead className="text-zinc-400">Organisme</TableHead>}
                                {tab !== 'interne' && <TableHead className="text-zinc-400">Delai</TableHead>}
                                <TableHead className="text-zinc-400">Quantite</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                {tab !== 'interne' && <TableHead className="text-zinc-400">Prerequis</TableHead>}
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map(r => {
                                const st = statusConfig[r.status] || statusConfig.disponible;
                                return (
                                    <TableRow key={r.id} className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50"
                                        onClick={() => setDetailResource(r)} data-testid={`resource-row-${r.id}`}>
                                        <TableCell className="font-medium text-white">
                                            {r.name}
                                            {r.location_name && <p className="text-[10px] text-zinc-500">{r.location_name}</p>}
                                        </TableCell>
                                        <TableCell className="text-zinc-300 text-sm">{typeLabel(r.type)}</TableCell>
                                        {tab !== 'interne' && (
                                            <TableCell>
                                                <span className="text-sm text-white">{r.organization_name || '-'}</span>
                                                {r.contact_name && <p className="text-[10px] text-zinc-500">{r.contact_name}</p>}
                                            </TableCell>
                                        )}
                                        {tab !== 'interne' && (
                                            <TableCell>
                                                {r.availability_delay ? (
                                                    <span className="flex items-center gap-1 text-sm text-amber-400">
                                                        <Clock className="w-3 h-3" />{r.availability_delay}
                                                    </span>
                                                ) : '-'}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <span className="font-mono text-white">{r.quantity_available}</span>
                                            <span className="text-zinc-500 text-xs">/{r.quantity}</span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                                        </TableCell>
                                        {tab !== 'interne' && (
                                            <TableCell className="text-xs text-zinc-400 max-w-[150px] truncate">
                                                {r.prerequisites || '-'}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                {tab !== 'interne' && canManageResources && (
                                                    <Button size="sm" variant="ghost" onClick={() => handleMobilize(r.id)}
                                                        className={`text-[10px] h-6 px-2 ${r.status === 'mobilise' ? 'text-zinc-400' : 'text-amber-400'}`}
                                                        data-testid={`mobilize-${r.id}`}>
                                                        <Zap className="w-3 h-3 mr-1" />
                                                        {r.status === 'mobilise' ? 'Demobiliser' : 'Mobiliser'}
                                                    </Button>
                                                )}
                                                {tab === 'interne' && canConsumeResources && r.quantity_available > 0 && (
                                                    <Button size="sm" variant="ghost" onClick={() => { setSelectedResource(r); setConsumeQty(1); setConsumeDialogOpen(true); }}
                                                        className="text-[10px] h-6 px-2 text-amber-400" data-testid={`consume-${r.id}`}>
                                                        <Minus className="w-3 h-3 mr-1" />Consommer
                                                    </Button>
                                                )}
                                                {canManageResources && (
                                                    <>
                                                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-6 w-6 p-0 text-zinc-400" data-testid={`edit-${r.id}`}>
                                                            <Edit className="w-3 h-3" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} className="h-6 w-6 p-0 text-red-400" data-testid={`delete-${r.id}`}>
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={tab === 'interne' ? 5 : 8} className="text-center py-12 text-zinc-500">
                                        <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                        <p>Aucune ressource dans cette categorie</p>
                                        {canManageResources && (
                                            <Button className="mt-3 tactical-btn text-sm" onClick={() => openCreate(tab)}>
                                                <Plus className="w-4 h-4 mr-2" /> Ajouter une ressource
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* CREATE / EDIT DIALOG */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditingResource(null); } }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="resource-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingResource ? 'Modifier' : 'Ajouter'} une ressource</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="form-label">Nom</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="tactical-input" required data-testid="resource-name" />
                            </div>
                            <div>
                                <Label className="form-label">Type</Label>
                                <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                                    <SelectTrigger className="tactical-input" data-testid="resource-type"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        {resourceTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="form-label">Categorie</Label>
                                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                                    <SelectTrigger className="tactical-input" data-testid="resource-category"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        <SelectItem value="interne">Moyen Interne</SelectItem>
                                        <SelectItem value="contrat_externe">Contrat Externe</SelectItem>
                                        <SelectItem value="association">Association</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="form-label">Statut</Label>
                                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                                    <SelectTrigger className="tactical-input" data-testid="resource-status"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label className="form-label">Quantite totale</Label>
                                <Input type="number" min="0" value={formData.quantity}
                                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                                    className="tactical-input" data-testid="resource-quantity" />
                            </div>
                            <div>
                                <Label className="form-label">Disponible</Label>
                                <Input type="number" min="0" value={formData.quantity_available}
                                    onChange={(e) => setFormData({...formData, quantity_available: parseInt(e.target.value) || 0})}
                                    className="tactical-input" data-testid="resource-available" />
                            </div>
                            <div>
                                <Label className="form-label">Localisation</Label>
                                <Input value={formData.location_name}
                                    onChange={(e) => setFormData({...formData, location_name: e.target.value})}
                                    className="tactical-input" placeholder="Depot, centre..." />
                            </div>
                        </div>

                        {formData.category !== 'interne' && (
                            <>
                                <div className="border-t border-zinc-800 pt-4">
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">
                                        {formData.category === 'contrat_externe' ? 'Informations Contrat' : 'Informations Association'}
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="form-label">Nom de l'organisme</Label>
                                        <Input value={formData.organization_name}
                                            onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                                            className="tactical-input" placeholder="Ex: Bouygues TP" data-testid="resource-org" />
                                    </div>
                                    <div>
                                        <Label className="form-label">Delai de disponibilite</Label>
                                        <Input value={formData.availability_delay}
                                            onChange={(e) => setFormData({...formData, availability_delay: e.target.value})}
                                            className="tactical-input" placeholder="Ex: 2h, 24h, 48h" data-testid="resource-delay" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="form-label">Contact referent</Label>
                                        <Input value={formData.contact_name}
                                            onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                                            className="tactical-input" placeholder="Nom du contact" />
                                    </div>
                                    <div>
                                        <Label className="form-label">Telephone contact</Label>
                                        <Input value={formData.contact_phone}
                                            onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                                            className="tactical-input" placeholder="06..." />
                                    </div>
                                </div>
                                <div>
                                    <Label className="form-label">Prerequis / Competences requises</Label>
                                    <Input value={formData.prerequisites}
                                        onChange={(e) => setFormData({...formData, prerequisites: e.target.value})}
                                        className="tactical-input" placeholder="Ex: CACES grue, Permis C, Permis tracteur..."
                                        data-testid="resource-prereqs" />
                                </div>
                                {formData.category === 'contrat_externe' && (
                                    <div>
                                        <Label className="form-label">Conditions du contrat</Label>
                                        <Textarea value={formData.contract_conditions}
                                            onChange={(e) => setFormData({...formData, contract_conditions: e.target.value})}
                                            className="tactical-input min-h-[60px]" placeholder="Horaires, limitations..." />
                                    </div>
                                )}
                            </>
                        )}

                        <div>
                            <Label className="form-label">Notes</Label>
                            <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                className="tactical-input min-h-[60px]" />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); setEditingResource(null); }}>Annuler</Button>
                            <Button type="submit" className="tactical-btn" data-testid="resource-submit">
                                {editingResource ? 'Enregistrer' : 'Ajouter'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* DETAIL DIALOG */}
            <Dialog open={!!detailResource} onOpenChange={(open) => { if (!open) setDetailResource(null); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="resource-detail">
                    {detailResource && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Package className="w-5 h-5 text-cyan-500" />
                                    {detailResource.name}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 mt-2">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-2.5 bg-zinc-800/50 rounded">
                                        <p className="text-[10px] text-zinc-500 uppercase">Type</p>
                                        <p className="text-sm text-white">{typeLabel(detailResource.type)}</p>
                                    </div>
                                    <div className="p-2.5 bg-zinc-800/50 rounded">
                                        <p className="text-[10px] text-zinc-500 uppercase">Statut</p>
                                        <Badge className={`text-xs ${(statusConfig[detailResource.status] || statusConfig.disponible).color}`}>
                                            {(statusConfig[detailResource.status] || statusConfig.disponible).label}
                                        </Badge>
                                    </div>
                                    <div className="p-2.5 bg-zinc-800/50 rounded">
                                        <p className="text-[10px] text-zinc-500 uppercase">Quantite</p>
                                        <p className="text-sm"><span className="text-white font-mono">{detailResource.quantity_available}</span><span className="text-zinc-500">/{detailResource.quantity}</span></p>
                                    </div>
                                    <div className="p-2.5 bg-zinc-800/50 rounded">
                                        <p className="text-[10px] text-zinc-500 uppercase">Localisation</p>
                                        <p className="text-sm text-white">{detailResource.location_name || centerName(detailResource.center_id) || '-'}</p>
                                    </div>
                                </div>
                                {(detailResource.category || 'interne') !== 'interne' && (
                                    <>
                                        <div className="border-t border-zinc-800 pt-3">
                                            <p className="text-xs text-cyan-400 uppercase tracking-wider mb-2">
                                                {detailResource.category === 'contrat_externe' ? 'Contrat Externe' : 'Association'}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="p-2.5 bg-zinc-800/50 rounded">
                                                <p className="text-[10px] text-zinc-500 uppercase">Organisme</p>
                                                <p className="text-sm text-white">{detailResource.organization_name || '-'}</p>
                                            </div>
                                            <div className="p-2.5 bg-zinc-800/50 rounded">
                                                <p className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> Delai</p>
                                                <p className="text-sm text-amber-400 font-semibold">{detailResource.availability_delay || '-'}</p>
                                            </div>
                                        </div>
                                        {detailResource.prerequisites && (
                                            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded">
                                                <p className="text-[10px] text-amber-400 uppercase flex items-center gap-1"><Shield className="w-3 h-3" /> Prerequis</p>
                                                <p className="text-sm text-white mt-1">{detailResource.prerequisites}</p>
                                            </div>
                                        )}
                                        {detailResource.contact_name && (
                                            <div className="p-2.5 bg-zinc-800/50 rounded">
                                                <p className="text-[10px] text-zinc-500 uppercase">Contact referent</p>
                                                <p className="text-sm text-white">{detailResource.contact_name}</p>
                                                {detailResource.contact_phone && (
                                                    <a href={`tel:${detailResource.contact_phone}`} className="text-sm text-blue-400 flex items-center gap-1 mt-1">
                                                        <Phone className="w-3 h-3" />{detailResource.contact_phone}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {detailResource.contract_conditions && (
                                            <div className="p-2.5 bg-zinc-800/50 rounded">
                                                <p className="text-[10px] text-zinc-500 uppercase">Conditions du contrat</p>
                                                <p className="text-sm text-white whitespace-pre-wrap">{detailResource.contract_conditions}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                                {detailResource.notes && (
                                    <div className="p-2.5 bg-zinc-800/50 rounded">
                                        <p className="text-[10px] text-zinc-500 uppercase">Notes</p>
                                        <p className="text-sm text-white">{detailResource.notes}</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* CONSUME DIALOG */}
            <Dialog open={consumeDialogOpen} onOpenChange={setConsumeDialogOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Consommer: {selectedResource?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-zinc-400">Disponible: <span className="text-white font-mono">{selectedResource?.quantity_available}</span></p>
                        <div>
                            <Label className="form-label">Quantite a consommer</Label>
                            <Input type="number" min="1" max={selectedResource?.quantity_available || 1} value={consumeQty}
                                onChange={(e) => setConsumeQty(parseInt(e.target.value) || 1)} className="tactical-input" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => setConsumeDialogOpen(false)}>Annuler</Button>
                            <Button onClick={handleConsume} className="bg-amber-600 hover:bg-amber-500" data-testid="consume-submit">Consommer</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* MOBILIZATION HISTORY DIALOG */}
            <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh]" data-testid="mobilization-history">
                    <DialogHeader>
                        <div className="flex items-center justify-between w-full">
                            <DialogTitle className="flex items-center gap-2">
                                <History className="w-5 h-5 text-cyan-500" />
                                Historique des mobilisations
                            </DialogTitle>
                            {mobilizationHistory.length > 0 && (
                                <a href={`${API}/resources/mobilization-history/export?token=${token}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30 transition-colors"
                                    data-testid="export-resource-history-btn">
                                    <FileDown className="w-3.5 h-3.5" /> Exporter CSV
                                </a>
                            )}
                        </div>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh] mt-2 space-y-2">
                        {mobilizationHistory.length > 0 ? mobilizationHistory.map((h, i) => (
                            <div key={h.id || i} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-white font-medium">{h.resource_name}</span>
                                        {h.organization_name && <span className="text-xs text-zinc-500">({h.organization_name})</span>}
                                        <Badge className={`text-[10px] ${h.action === 'mobilise' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                            {h.action === 'mobilise' ? 'Mobilise' : 'Demobilise'}
                                        </Badge>
                                    </div>
                                    <span className="text-[10px] text-zinc-500">
                                        {new Date(h.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                                    </span>
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-1">par {h.changed_by}</p>
                            </div>
                        )) : (
                            <p className="text-zinc-500 text-center py-8">Aucune mobilisation enregistree</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ResourcesPage;
